import { Button, Tooltip } from '@ghs/components';
import { Status, Wrapper } from '@googlemaps/react-wrapper';
import { Container, Dialog, DialogContent, DialogTitle, IconButton, Stack, Typography } from '@mui/material';
import { useUnit } from 'effector-react';
import { useNavigate } from 'react-router-dom';
import { DeliveryMap } from '../../components/DeliveryMap';
import { $$tutorialVideos } from '../../components/TutorialVideosModal/model';
import TutorialVideoButton from '../../components/TutorialVideosModal/TutorialVideoButton';
import useMountedEvent from '../../hooks/useMountedEvent';
import { $$personaService } from '../../services/PersonaService/model';
import AcsEvents from './AcsEvents';
import MailingInformation from './MailingInformation';
import { $$splResultsPage } from './model';
import MailingDetails from './ScanDetails';
import SplResultsAlerts from './SplResultsAlerts';
import CloseIcon from '@mui/icons-material/Close';
import { ReactNode } from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

export default function SplResultsPage() {
  useMountedEvent($$splResultsPage.pageMounted);

  const navigate = useNavigate();

  const acsEventDialogData = useUnit($$splResultsPage.$acsEventDialogData);
  const acsEventDialogIsOpen = useUnit($$splResultsPage.$acsEventDialogIsOpen);
  const isLoading = useUnit($$splResultsPage.$isLoading);
  const isExportingPdf = useUnit($$splResultsPage.$isExportingPdf);
  const activePersonaGhsSystem = useUnit($$personaService.$activePersona.map(persona => persona?.ghsSystem || null));

  return (
    <Container data-testid="spl-results-container" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <TutorialVideoButton url={$$tutorialVideos.links[activePersonaGhsSystem === 'MAP' ? 'SPL:MT_PERSONA' : 'SPL:ST_PERSONA']} />

      <Typography variant="h4" color="text.primary">
        Single Piece Lookup
      </Typography>
      <SplResultsAlerts />

      <Stack direction="row" spacing={2}>
        <Button variant="outlined" size="medium" color="primary" onClick={() => navigate(-1)}>
          Back
        </Button>
        <Button color="primary" variant="contained" onClick={() => $$splResultsPage.exportPdfClicked()} disabled={isLoading || isExportingPdf}>
          Export to PDF
        </Button>
      </Stack>
      <MailingInformation />
      <AcsEvents />
      <MailingDetails />
      <Wrapper apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY} render={MapRenderer}>
        <DeliveryMap />
      </Wrapper>
      <Dialog
        data-testid="acs-event-details-dialog"
        open={acsEventDialogIsOpen}
        onClose={$$splResultsPage.acsEventDialogClose}
        sx={{ m: 0, p: 3 }}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: 'auto', // Allow the Paper to resize based on its content
            maxWidth: 'none' // Prevent the Paper from constraining its width
          }
        }}
      >
        <DialogTitle>
          <Typography variant="h5" display="inline">
            ACS Event
          </Typography>
          <IconButton
            aria-label="close"
            onClick={$$splResultsPage.acsEventDialogClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'primary.main'
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex' }}>
          <Stack direction="row" spacing={22}>
            <Stack direction="column" spacing={2}>
              <AcsEventDetailsLabel label="ACS Event Received" />
              <AcsEventDetailsLabel label="Reason" />
              <AcsEventDetailsLabel label="Deliverability Code" />
              <AcsEventDetailsLabel label="COA Name" />
              <AcsEventDetailsLabel label="New Address" />
              <AcsEventDetailsLabel label="Move Type" />
              <AcsEventDetailsLabel label="New Address Type" />
              <AcsEventDetailsLabel label="Move Effective Date" />
              <AcsEventDetailsLabel label="Made Available Date" />
              <AcsEventDetailsLabel label="USPS Site ID" />
              <AcsEventDetailsLabel label="USPS Old Address" />
            </Stack>
            <Stack direction="column" spacing={2}>
              <AcsEventDetailsValue>{acsEventDialogData ? acsEventDialogData.dateReceived : ''}</AcsEventDetailsValue>
              <AcsEventDetailsValue>{acsEventDialogData ? acsEventDialogData.reason : ''}</AcsEventDetailsValue>
              <AcsEventDetailsValue>{acsEventDialogData ? acsEventDialogData.uspsDeliverabilityCode : ''}</AcsEventDetailsValue>
              <AcsEventDetailsValue>{acsEventDialogData ? acsEventDialogData.uspsCOAName : ''}</AcsEventDetailsValue>
              <AcsEventDetailsValue>{acsEventDialogData ? acsEventDialogData.eventDetails.address : ''}</AcsEventDetailsValue>
              <AcsEventDetailsValue>{acsEventDialogData ? acsEventDialogData.eventDetails.moveType : ''}</AcsEventDetailsValue>
              <AcsEventDetailsValue>{acsEventDialogData ? acsEventDialogData.eventDetails.uspsNewAddressType : ''}</AcsEventDetailsValue>
              <AcsEventDetailsValue>{acsEventDialogData ? acsEventDialogData.eventDetails.moveEffectiveDate : ''}</AcsEventDetailsValue>
              <AcsEventDetailsValue>{acsEventDialogData ? acsEventDialogData.eventDetails.madeAvailableDate : ''}</AcsEventDetailsValue>
              <AcsEventDetailsValue>{acsEventDialogData ? acsEventDialogData.eventDetails.uspsSiteID : ''}</AcsEventDetailsValue>
              <AcsEventDetailsValue>{acsEventDialogData ? acsEventDialogData.eventDetails.uspsOldAddress : ''}</AcsEventDetailsValue>
            </Stack>
          </Stack>
        </DialogContent>
      </Dialog>
    </Container>
  );
}

function AcsEventDetailsLabel({ label, tooltip, tooltipPlacement = 'right-start' }: { label: string; tooltip?: string; tooltipPlacement?: string }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Typography sx={{ color: 'text.secondary' }} noWrap>
        {label}:
      </Typography>
      {tooltip && (
        <Tooltip
          title={tooltip}
          placement={tooltipPlacement}
          PopperProps={{
            sx: {
              '& .MuiTooltip-tooltip': {
                maxWidth: 150,
                whiteSpace: 'normal'
              }
            }
          }}
        >
          <InfoOutlinedIcon fontSize="small" />
        </Tooltip>
      )}
    </Stack>
  );
}

function AcsEventDetailsValue({ children }: { children: ReactNode }) {
  return (
    <Typography
      sx={{
        flex: 1,
        color: 'text.secondary'
      }}
    >
      {children}
    </Typography>
  );
}

function MapRenderer(status: Status) {
  if (status === Status.LOADING || status === Status.FAILURE) {
    return (
      <Typography variant="h5" color="text.primary">
        {status} ..
      </Typography>
    );
  }

  return <Typography />;
}
